import { createGenericStore } from '@/stores/utils/genericStore.js'
import axios from 'axios'

const normalizeChecklistTemplateablePayload = (payload = {}, { required = false } = {}) => {
  const checklistTemplateableId = Number(payload.checklist_templateable_id ?? 0)
  const checklistTemplateableType = String(payload.checklist_templateable_type ?? '').trim()

  if (checklistTemplateableId > 0 && checklistTemplateableType.length > 0) {
    return {
      checklist_templateable_id: checklistTemplateableId,
      checklist_templateable_type: checklistTemplateableType,
    }
  }

  if (required) {
    throw new Error('A valid checklist template attachment target is required.')
  }

  return {}
}

export const useChecklistTemplatesStore = createGenericStore('checklistTemplates', '/api/v1/checklist-templates', {
  actions: {
    async startEditChecklistTemplate(checklistTemplateId) {
      const normalizedChecklistTemplateId = Number(checklistTemplateId)

      if (!Number.isFinite(normalizedChecklistTemplateId) || normalizedChecklistTemplateId <= 0) {
        throw new Error('A valid checklist template is required to start editing.')
      }

      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.post(`${this.api}/${normalizedChecklistTemplateId}/start-edit`)
        this.itemPatchState(data)

        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async beginChecklistTemplateEditing(checklistTemplateId) {
      const normalizedChecklistTemplateId = Number(checklistTemplateId)

      if (!Number.isFinite(normalizedChecklistTemplateId) || normalizedChecklistTemplateId <= 0) {
        throw new Error('A valid checklist template is required to start editing.')
      }

      const checklistTemplate = await this.fetchItemById(normalizedChecklistTemplateId)

      if (!checklistTemplate) {
        return null
      }

      if (checklistTemplate.status === 'draft') {
        return this.fetchItemById(checklistTemplate.id)
      }

      const activeDraftChecklistTemplateId = Number(checklistTemplate?.active_draft?.id ?? 0)

      if (activeDraftChecklistTemplateId > 0) {
        const activeDraftChecklistTemplate = await this.fetchItemById(activeDraftChecklistTemplateId)

        this.itemPatchState({
          ...checklistTemplate,
          active_draft: activeDraftChecklistTemplate,
        })

        return activeDraftChecklistTemplate
      }

      if (checklistTemplate.status !== 'published') {
        throw new Error('Only published checklist templates can be edited.')
      }

      const draftChecklistTemplate = await this.startEditChecklistTemplate(checklistTemplate.id)

      this.itemPatchState({
        ...checklistTemplate,
        active_draft: draftChecklistTemplate,
      })

      return draftChecklistTemplate
    },

    async publishChecklistTemplate(checklistTemplateId) {
      const normalizedChecklistTemplateId = Number(checklistTemplateId)

      if (!Number.isFinite(normalizedChecklistTemplateId) || normalizedChecklistTemplateId <= 0) {
        throw new Error('A valid checklist template draft is required to publish.')
      }

      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.post(`${this.api}/${normalizedChecklistTemplateId}/publish`)
        this.itemPatchState(data)

        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async abortChecklistTemplate(checklistTemplateId) {
      const normalizedChecklistTemplateId = Number(checklistTemplateId)

      if (!Number.isFinite(normalizedChecklistTemplateId) || normalizedChecklistTemplateId <= 0) {
        throw new Error('A valid checklist template draft is required to abort.')
      }

      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.post(`${this.api}/${normalizedChecklistTemplateId}/abort`)

        this.itemRemoveFromState(normalizedChecklistTemplateId)

        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async attachChecklistTemplate(checklistTemplateId, checklistTemplateablePayload = {}) {
      const normalizedChecklistTemplateId = Number(checklistTemplateId)

      if (!Number.isFinite(normalizedChecklistTemplateId) || normalizedChecklistTemplateId <= 0) {
        throw new Error('A valid checklist template is required to attach.')
      }

      this.isLoading = true
      this.error = null

      try {
        const payload = normalizeChecklistTemplateablePayload(checklistTemplateablePayload, { required: true })
        const { data } = await axios.post(`${this.api}/${normalizedChecklistTemplateId}/attach`, {
          ...payload,
        })

        this.itemPatchState(data)

        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async detachChecklistTemplate(checklistTemplateId, checklistTemplateablePayload = {}) {
      const normalizedChecklistTemplateId = Number(checklistTemplateId)

      if (!Number.isFinite(normalizedChecklistTemplateId) || normalizedChecklistTemplateId <= 0) {
        throw new Error('A valid checklist template is required to detach.')
      }

      this.isLoading = true
      this.error = null

      try {
        const payload = normalizeChecklistTemplateablePayload(checklistTemplateablePayload, { required: true })
        await axios.post(`${this.api}/${normalizedChecklistTemplateId}/detach`, {
          ...payload,
        })

        this.itemRemoveFromState(normalizedChecklistTemplateId)

        return true
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async duplicateChecklistTemplate(checklistTemplateId, payload = {}) {
      const normalizedChecklistTemplateId = Number(checklistTemplateId)

      if (!Number.isFinite(normalizedChecklistTemplateId) || normalizedChecklistTemplateId <= 0) {
        throw new Error('A valid checklist template is required to duplicate.')
      }

      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.post(
          `${this.api}/${normalizedChecklistTemplateId}/duplicate`,
          normalizeChecklistTemplateablePayload(payload),
        )

        this.itemPatchState(data)

        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async importChecklistTemplateItems(checklistTemplateId, content) {
      const normalizedChecklistTemplateId = Number(checklistTemplateId)
      const normalizedContent = String(content ?? '').trim()

      if (!Number.isFinite(normalizedChecklistTemplateId) || normalizedChecklistTemplateId <= 0) {
        throw new Error('A valid checklist template is required to import items.')
      }

      if (normalizedContent.length === 0) {
        throw new Error('Paste the checklist items you want to import.')
      }

      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.post(`${this.api}/${normalizedChecklistTemplateId}/import-items`, {
          content: normalizedContent,
        })

        this.itemPatchState(data)

        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isLoading = false
      }
    },

  },
})
